import React, { useEffect } from 'react';

import CrossIcon from 'icons/ic-close.svg';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';
import QRCode from 'react-qr-code';

import { oneTimePasswordShape } from 'components/hooks/useOneTimePassword';
import { validationErrorsShape } from 'components/hooks/useValidationErrors';

import CopyableField from 'components/application/CopyableField';
import ModalBox from 'components/application/ModalBox';

import OneTimePasswordSignInCard from 'components/users/twoFactorAuthentication/OneTimePasswordSignInCard';
import ValidationErrorsMessages from 'components/validationErrors/components/ValidationErrorsMessages';

export default function EnableModal({
  closeModal,
  isDismissible = true,
  isOpen,
  onFormSubmit,
  oneTimePasswordProps,
  provisioningUri,
  validationErrorsProps,
}) {
  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'settings.profile.twoFactorAuthentication.EnableModal',
  });

  const { t: i18nTAuthentication } = useTranslation('common', {
    keyPrefix: 'authentication',
  });

  useEffect(() => {
    oneTimePasswordProps.inputRefs[0].current.focus();
  }, [isOpen]);

  return (
    <ModalBox
      isOpen={isOpen}
      mode='flexible'
      onClose={isDismissible ? closeModal : () => {}}
      usingWideDimensions={true}
      withFooter={false}
    >
      <div
        className={`tw-flex tw-justify-between tw-p-6 ${isDismissible ? 'tw-items-center' : 'tw-flex-col'}`}
      >
        <h2 className='tw-m-0 tw-w-96 tw-text-xl tw-font-semibold tw-text-grey-900'>
          {i18nTAuthentication('setUpTwoFactor')}
        </h2>
        {isDismissible ?
          <CrossIcon
            className='tw-block tw-cursor-pointer tw-object-right'
            data-element-name='close-modal'
            onClick={closeModal}
          />
        : <div className='tw-mt-2'>
            {i18nTAuthentication('twoFactorAuthenticationIsRequired')}
          </div>
        }
      </div>
      <hr className='tw-my-0 tw-w-full' />
      <div className='tw-mb-12 tw-ml-16 tw-mr-16 tw-mt-10'>
        {Object.keys(validationErrorsProps.validationErrors).length > 0 && (
          <ValidationErrorsMessages
            className='tw-mb-6 tw-border-1 tw-border-solid tw-border-red-200'
            validationErrors={validationErrorsProps.validationErrors}
          />
        )}
        <div className='tw-mb-10 tw-flex'>
          <span className='tw-text-sm tw-mr-2 tw-font-semibold tw-text-grey-700'>
            1.
          </span>
          <div>
            <p className='tw-text-sm tw-mb-3 tw-font-semibold tw-text-grey-700'>
              {i18nT('stepOneTitle')}
            </p>
            <p className='tw-m-0 tw-mt-1 tw-text-m tw-text-grey-700'>
              <Trans
                components={{ bold: <strong /> }}
                i18nKey='settings.profile.twoFactorAuthentication.EnableModal.stepOneDescription'
                ns='components'
              />
            </p>
          </div>
        </div>

        <div className='tw-mb-10 tw-flex'>
          <span className='tw-text-sm tw-mr-2 tw-font-semibold tw-text-grey-700'>
            2.
          </span>
          <div className='tw-w-full'>
            <p className='tw-text-sm tw-mb-3 tw-font-semibold tw-text-grey-700'>
              {i18nT('stepTwoTitle')}
            </p>
            {provisioningUri && (
              <>
                <div className='tw-mb-3 tw-block tw-text-center'>
                  <QRCode
                    className='tw-mb-1 tw-mt-2 tw-h-40 tw-w-40'
                    value={provisioningUri}
                  />
                </div>
                <p className='tw-m-0 tw-mb-2 tw-mt-1 tw-text-m tw-text-grey-700'>
                  {i18nT('stepTwoDescription')}
                </p>
                <CopyableField
                  name='secret'
                  value={getSecretFromUri(provisioningUri)}
                />
              </>
            )}
          </div>
        </div>

        <div className='tw-flex'>
          <span className='tw-text-sm tw-mr-2 tw-font-semibold tw-text-grey-700'>
            3.
          </span>
          <div>
            <p className='tw-text-sm tw-mb-3 tw-font-semibold tw-text-grey-700'>
              {i18nT('stepThreeTitle')}
            </p>
            <p className='tw-m-0 tw-mb-2 tw-mt-1 tw-text-m tw-text-grey-700'>
              {i18nT('stepThreeDescription')}
            </p>
            <OneTimePasswordSignInCard
              inputRefs={oneTimePasswordProps.inputRefs}
              onFormPasteCapture={oneTimePasswordProps.handleFormPasteCapture}
              onInputClick={oneTimePasswordProps.handleInputClick}
              onInputKeyUp={oneTimePasswordProps.handleInputKeyUp}
              onSubmit={onFormSubmit}
              otpCode={oneTimePasswordProps.otpCode}
            />
          </div>
        </div>
      </div>
    </ModalBox>
  );
}

function getSecretFromUri(provisioningUri) {
  const secretKey = new URL(provisioningUri).searchParams.get('secret');

  return secretKey;
}

EnableModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  isDismissible: PropTypes.bool,
  isOpen: PropTypes.bool.isRequired,
  oneTimePasswordProps: oneTimePasswordShape,
  onFormSubmit: PropTypes.func.isRequired,
  provisioningUri: PropTypes.string.isRequired,
  validationErrorsProps: validationErrorsShape,
};
